import {
  FieldRichText,
  FieldText,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { ChangeEvent, Component } from 'react';
import { v4 as uuid } from 'uuid';

import { SanitizeHTML } from 'app/utils/htmlSanitizer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import { WidgetPreview } from '../../../conversation/components/widgetPreview';
import type {
  FlowChatNodeChatInputPayloadT,
  FlowChatNodeChatInputT,
} from '../nodeTypes';

type PropsT = FlowManagerEditModalComponentT<FlowChatNodeChatInputT>;

type StateT = {
  errors: ErrorsT;
  hasChanges: boolean;
  isDirty: boolean;
  payload: FlowChatNodeChatInputPayloadT;
};

type ErrorsT = Partial<
  Pick<FlowChatNodeChatInputPayloadT, 'field_label' | 'message'>
>;

export default class ChatInputModal extends Component<PropsT, StateT> {
  constructor(props: PropsT) {
    super(props);
    let payload: FlowChatNodeChatInputPayloadT;

    if (this.props.node.payload !== null) {
      payload = this.props.node.payload;
    } else {
      payload = {
        field_label: '',
        field_placeholder: '',
        message: '',
        nextNodeId: uuid(),
      };
    }

    this.state = {
      errors: {},
      hasChanges: false,
      isDirty: false,
      payload,
    };
  }

  @boundMethod
  prepareNode(): FlowChatNodeChatInputT {
    return {
      ...this.props.node,
      badges: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.CHAT_INPUT,
      overlay: [],
      payload: this.state.payload,
      text: 'Input',
    };
  }

  updatePayload(updateObj: Partial<FlowChatNodeChatInputPayloadT>): void {
    this.setState(
      {
        hasChanges: true,
        payload: { ...this.state.payload, ...updateObj },
      },
      () => {
        if (this.state.isDirty) {
          this.validate();
        }
      },
    );
  }

  @boundMethod
  handleMessageChange(message: undefined | string): void {
    this.updatePayload({ message });
  }

  @boundMethod
  handleLabelChange(event?: ChangeEvent<HTMLInputElement>): void {
    this.updatePayload({ field_label: event?.target.value });
  }

  @boundMethod
  handlePlaceholderChange(event?: ChangeEvent<HTMLInputElement>): void {
    this.updatePayload({ field_placeholder: event?.target.value });
  }

  @boundMethod
  handleCancel(): void {
    this.props.triggerClose();
  }

  @boundMethod
  handleSave(): void {
    const { replaceNode, triggerClose } = this.props;

    if (this.validate()) {
      replaceNode(this.prepareNode());
      triggerClose();
      this.setState({
        hasChanges: false,
      });
    } else {
      this.setState({
        isDirty: true,
      });
    }
  }

  validate(): boolean {
    const {
      payload: { message, field_label },
    } = this.state;
    const errors: ErrorsT = {};
    if (message.trim() === '') {
      errors.message = 'Please fill out a message';
    }
    if (field_label.trim() === '') {
      errors.field_label = 'Please fill out a label';
    }
    this.setState({
      errors,
    });
    return Object.keys(errors).length === 0;
  }

  render() {
    const {
      errors,
      payload: { message, field_label, field_placeholder },
    } = this.state;
    const { node, nodeLabel } = this.props;

    return (
      <div className="conversational-modal modal-chatflow-holder">
        <ModalHead title={nodeLabel} onClose={this.handleCancel}>
          The &quot;Input&quot; action allows you to ask customers for specific
          information (e.g. customer number or order number).
        </ModalHead>
        <ModalBody>
          <div className="conversational-modal__columns">
            <div className="conversational-modal__column">
              <WidgetPreview
                input={{
                  label: field_label,
                  placeholder: field_placeholder,
                }}
                message={message}
                className="conversational-modal__preview"
              />
            </div>
            <div className="conversational-modal__column">
              <FieldRichText
                required
                allowFormatting={true}
                sanitize={SanitizeHTML.sanitizerForProfile('widgetchat')}
                className="conversational-modal__input"
                label="Your message"
                meta={{ error: errors.message }}
                input={{
                  name: 'message',
                  onChange: this.handleMessageChange,
                  value: message,
                }}
              />
              <FieldText
                required
                className="conversational-modal__input"
                label="Field label"
                placeholder="E.g. customer number"
                meta={{ error: errors.field_label }}
                input={{
                  name: 'label',
                  onChange: this.handleLabelChange,
                  value: field_label,
                }}
              />
              <FieldText
                className="conversational-modal__input"
                label="Field placeholder"
                placeholder="E.g. XXXX-XXXX-XXXX"
                meta={{}}
                input={{
                  name: 'placeholder',
                  onChange: this.handlePlaceholderChange,
                  value: field_placeholder,
                }}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFoot
          confirmOnClick={this.handleSave}
          confirmName={getConfirmButtonVerb(node)}
          cancelOnClick={this.handleCancel}
        />
      </div>
    );
  }
}
